import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bot,
  RefreshCw,
  CheckCircle2,
  Clock,
  Mail,
  Inbox,
  Power,
  ChevronRight,
  Loader2,
  Send,
} from "lucide-react";
import { useTranslation, Trans } from "react-i18next";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";

function StatusBadge({ status }) {
  const styles = {
    pending: "bg-amber-50 text-amber-800 border-amber-200",
    preview: "bg-zinc-100 text-zinc-700 border-zinc-200",
    sending: "bg-blue-50 text-blue-700 border-blue-200",
    sent: "bg-emerald-50 text-emerald-700 border-emerald-200",
    failed: "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.14em] ${styles[status] || styles.preview}`}
      data-testid={`log-status-${status}`}
    >
      {status}
    </span>
  );
}

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

function timeUntil(iso) {
  if (!iso) return "—";
  const diff = (new Date(iso).getTime() - Date.now()) / 1000;
  if (diff <= 0) return "now";
  if (diff < 60) return `${Math.round(diff)}s`;
  if (diff < 3600) return `${Math.round(diff / 60)}m`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h`;
  return `${Math.round(diff / 86400)}d`;
}

export default function AlertSystemSection({ onOpenLog }) {
  const { t } = useTranslation("dashboard");
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [provider, setProvider] = useState(null);

  const refresh = async () => {
    try {
      const [statusRes, logsRes, providerRes] = await Promise.all([
        api.get("/alerts/scheduler/status"),
        api.get("/alerts/email-logs", { params: { limit: 8 } }),
        api.get("/alerts/email-provider"),
      ]);
      setStatus(statusRes.data);
      setLogs(logsRes.data);
      setProvider(providerRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // Poll every 30s so the "Next run" countdown stays roughly accurate.
    const id = setInterval(refresh, 30000);
    return () => clearInterval(id);
  }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const res = await api.post("/alerts/scheduler/run");
      toast.success(
        `Scan complete · ${res.data.created} new${res.data.skipped ? `, ${res.data.skipped} already queued` : ""}`
      );
      await refresh();
    } catch (e) {
      toast.error("Could not run scheduler");
    } finally {
      setRunning(false);
    }
  };

  const sendLog = async (logId) => {
    setSendingId(logId);
    try {
      await api.post(`/alerts/email-logs/${logId}/send`);
      toast.success("Email sent");
      await refresh();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const httpStatus = e.response?.status;
      if (httpStatus === 503) {
        toast.error("Email provider not configured — set RESEND_API_KEY first");
      } else if (httpStatus === 409) {
        toast.message(detail || "This email is no longer pending");
        await refresh();
      } else if (httpStatus === 502) {
        toast.error(detail || "Provider rejected the email");
        await refresh();
      } else {
        toast.error(detail || "Could not send email");
      }
    } finally {
      setSendingId(null);
    }
  };

  if (loading) {
    return (
      <section className="bg-white border border-zinc-200 rounded-xl p-6 mb-10" data-testid="alert-system-loading">
        <p className="text-sm text-zinc-400">{t("page_title")}…</p>
      </section>
    );
  }

  const totalPreviews = (status?.user_counts?.total) ?? 0;
  const pending = status?.user_counts?.pending ?? 0;

  return (
    <section
      className="bg-white border border-zinc-200 rounded-xl overflow-hidden mb-10"
      data-testid="alert-system-section"
    >
      {/* Status header */}
      <div className="px-5 py-4 border-b border-zinc-200 flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <div
            className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${
              status?.enabled ? "bg-emerald-50" : "bg-zinc-100"
            }`}
          >
            <Bot
              size={16}
              strokeWidth={1.75}
              className={status?.enabled ? "text-emerald-700" : "text-zinc-500"}
            />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-display font-semibold text-lg tracking-tight">{t("alert_system.title")}</h2>
              <span
                data-testid="scheduler-status-pill"
                className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-medium px-2 py-0.5 rounded-full border ${
                  status?.enabled
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-zinc-100 text-zinc-600 border-zinc-200"
                }`}
              >
                <Power size={10} />
                {status?.enabled ? t("alert_system.active") : t("alert_system.offline")}
              </span>
            </div>
            <p className="text-xs text-zinc-500 mt-1 max-w-md">
              <Trans
                i18nKey="alert_system.description"
                t={t}
                values={{ interval: status?.interval_minutes, window: status?.window_days }}
                components={{
                  bold: <span className="text-zinc-900 font-medium" />,
                  success: <span className="text-emerald-700" />,
                }}
              />
            </p>
          </div>
        </div>
        <Button
          onClick={runNow}
          disabled={running}
          data-testid="run-scheduler-button"
          variant="outline"
          className="h-9 rounded-lg text-xs shrink-0"
        >
          {running ? (
            <>
              <Loader2 size={13} className="mr-1.5 animate-spin" /> {t("alert_system.scanning")}
            </>
          ) : (
            <>
              <RefreshCw size={13} className="mr-1.5" /> {t("alert_system.run_now")}
            </>
          )}
        </Button>
      </div>

      {/* Status grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-zinc-100 border-b border-zinc-200">
        <StatusCell
          label={t("alert_system.stats.last_run")}
          value={timeAgo(status?.last_run_at)}
          hint={
            status?.last_run_at &&
            t("alert_system.stats.last_run_hint", {
              created: status.last_run_created,
              skipped: status.last_run_skipped,
            })
          }
          testId="status-last-run"
        />
        <StatusCell
          label={t("alert_system.stats.next_run")}
          value={timeUntil(status?.next_run_at)}
          hint={status?.next_run_at ? t("alert_system.stats.next_run_hint") : "—"}
          testId="status-next-run"
        />
        <StatusCell
          label={t("alert_system.stats.pending")}
          value={pending}
          hint={t("alert_system.stats.pending_hint")}
          testId="status-pending"
          highlight={pending > 0}
        />
        <StatusCell
          label={t("alert_system.stats.total")}
          value={totalPreviews}
          hint={t("alert_system.stats.total_hint")}
          testId="status-total"
        />
      </div>

      {/* Recent logs */}
      <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display font-semibold text-sm tracking-tight truncate">{t("alert_system.previews_title")}</h3>
          <p className="text-xs text-zinc-500 mt-0.5">
            {t("alert_system.previews_subtitle")}
          </p>
        </div>
        {logs.length > 0 && (
          <button
            onClick={onOpenLog}
            data-testid="open-full-log-link"
            className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-0.5 shrink-0"
          >
            {t("alert_system.open_log")} <ChevronRight size={13} />
          </button>
        )}
      </div>

      {logs.length === 0 ? (
        <div className="p-10 text-center" data-testid="alert-empty">
          <div className="w-10 h-10 mx-auto rounded-lg bg-zinc-100 grid place-items-center mb-3">
            <Inbox size={16} className="text-zinc-500" />
          </div>
          <p className="text-sm text-zinc-500">
            {t("alert_system.empty")}
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-zinc-100" data-testid="alert-log-list">
          {logs.map((l) => (
            <li
              key={l.id}
              data-testid={`alert-log-row-${l.id}`}
              className="px-5 py-3 flex items-start gap-3 hover:bg-zinc-50 transition-colors"
            >
              <div className="w-8 h-8 rounded-md bg-amber-50 grid place-items-center shrink-0 mt-0.5">
                <Mail size={13} className="text-amber-700" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium text-zinc-900 truncate min-w-0" title={l.subject}>{l.subject}</p>
                  <StatusBadge status={l.status} />
                  {l.auto_generated && (
                    <span
                      data-testid="auto-badge"
                      className="inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-[10px] text-zinc-600 uppercase tracking-[0.14em]"
                    >
                      <Bot size={9} /> auto
                    </span>
                  )}
                </div>
                <div className="text-xs text-zinc-500 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="truncate max-w-full">
                    {t("alert_system.to_label")} <span className="text-zinc-900">{l.to}</span>
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Clock size={11} />
                    {timeAgo(l.generated_at)}
                  </span>
                  {l.days_remaining != null && (
                    <span>{t("alert_system.days_remaining", { count: l.days_remaining })}</span>
                  )}
                  {l.provider_id && (
                    <span className="font-mono text-[10px] text-zinc-400">id: {l.provider_id.slice(0, 12)}</span>
                  )}
                  <button
                    onClick={() => l.doc_id && navigate(`/analysis/${l.doc_id}`)}
                    className="text-zinc-500 hover:text-zinc-900 underline-offset-4 hover:underline"
                  >
                    {t("alert_system.open_lease")} ↗
                  </button>
                </div>
              </div>
              {l.status === "pending" && (
                <button
                  onClick={() => sendLog(l.id)}
                  disabled={sendingId === l.id || !provider?.configured}
                  data-testid={`send-email-button-${l.id}`}
                  title={
                    !provider?.configured
                      ? t("alert_system.send_tooltip_disabled")
                      : t("alert_system.send_tooltip_enabled")
                  }
                  className="shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-zinc-950 hover:bg-zinc-800 text-white text-[11px] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sendingId === l.id ? (
                    <>
                      <Loader2 size={12} className="animate-spin" /> {t("alert_system.sending")}
                    </>
                  ) : (
                    <>
                      <Send size={12} /> {t("alert_system.send")}
                    </>
                  )}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Footer banner */}
      <div className="px-5 py-3 bg-zinc-50 border-t border-zinc-200 text-[11px] text-zinc-500 flex items-center gap-2 flex-wrap">
        <CheckCircle2 size={12} className={provider?.configured ? "text-emerald-600" : "text-zinc-400"} />
        {provider?.configured ? (
          <span>
            <Trans
              i18nKey="alert_system.footer_configured"
              t={t}
              values={{ provider: provider.provider, email: provider.sender_email }}
              components={{ bold: <strong />, code: <code /> }}
            />
            {provider.sender_email === "onboarding@resend.dev" && (
              <> · {t("alert_system.footer_test_mode")}</>
            )}
          </span>
        ) : (
          <span>
            <Trans
              i18nKey="alert_system.footer_unconfigured"
              t={t}
              components={{ bold: <strong />, code: <code /> }}
            />
          </span>
        )}
      </div>
    </section>
  );
}

function StatusCell({ label, value, hint, testId, highlight }) {
  return (
    <div className="px-5 py-4 min-w-0" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-medium truncate" title={label}>{label}</p>
      <p
        className={`font-display text-xl font-semibold tracking-tight mt-1 truncate tabular-nums ${
          highlight ? "text-amber-700" : "text-zinc-950"
        }`}
        title={String(value)}
      >
        {value}
      </p>
      {hint && <p className="text-[10px] text-zinc-400 mt-0.5 truncate" title={hint}>{hint}</p>}
    </div>
  );
}
